var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
// @ts-expect-error: This library doesn't have type annotations.
import { diff_match_patch as DiffMatchPatch } from 'diff-match-patch';
import { Left } from 'purify-ts/Either';
import { EitherAsync } from 'purify-ts/EitherAsync';
import { Maybe } from 'purify-ts/Maybe';
import * as api from '@codegrade/api';
import { assert, map } from '@codegrade/utils';
import client from '@/client';
import { CoursePermissions as CPerm } from '@/permissions';
import { PeerFeedbackStore, SiteSettingsStore, store, SubmissionsStore, UserStore } from '@/store';
import * as utils from '@/utils';
import { CacheDecorator } from '@/utils/cache';
import { NEWLINE_CHAR } from '@/utils/diff';
import { NormalUser, User } from './user';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class FeedbackReplyEdit {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(id, editorId, oldText, newText, createdAt) {
        this.id = id;
        this.editorId = editorId;
        this.oldText = oldText;
        this.newText = newText;
        this.createdAt = createdAt;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data) {
        store.dispatch('users/addOrUpdateUser', { user: data.editor });
        return new FeedbackReplyEdit(data.id, data.editor.id, data.oldText, data.newText.orDefault(''), data.createdAt);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get editor() {
        return User.findUserById(this.editorId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getDiffHtml() {
        const dmp = new DiffMatchPatch();
        const diff = dmp.diff_main(this.oldText, this.newText);
        dmp.diff_cleanupSemantic(diff);
        return diff
            .map(([state, txt]) => {
            let cls = '';
            if (state === -1) {
                cls = 'removed';
            }
            else if (state === 1) {
                cls = 'added';
            }
            let innerTxt = txt;
            if (cls !== '') {
                // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
                const innerReplace = (match) => match.replace(/\n/g, `${NEWLINE_CHAR}\n`);
                innerTxt = txt.replace(/^\n+/, innerReplace).replace(/\n+$/, innerReplace);
            }
            return `<span class="${cls}">${utils.htmlEscape(innerTxt)}</span>`;
        })
            .join('');
    }
}
// This should really be kept track of in the store, but that isn't really
// possible for now without rewriting that entire store unfortunately.
// This contains a mapping between serverId and trackingId.
const trackingIdLookup = new Map();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class FeedbackReply {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(oldTrackingId, id, inReplyToId, message, authorId, lastEdit, replyType, createdAt, feedbackBaseId, commentType, approved, deleted) {
        this.id = id;
        this.inReplyToId = inReplyToId;
        this.message = message;
        this.authorId = authorId;
        this.lastEdit = lastEdit;
        this.replyType = replyType;
        this.createdAt = createdAt;
        this.feedbackBaseId = feedbackBaseId;
        this.commentType = commentType;
        this.approved = approved;
        this.deleted = deleted;
        const foundTrackingId = trackingIdLookup.get(id ?? -1);
        if (foundTrackingId != null) {
            this.trackingId = foundTrackingId;
        }
        else {
            if (this.id != null) {
                trackingIdLookup.set(this.id, oldTrackingId);
            }
            this.trackingId = oldTrackingId;
        }
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data, feedbackBaseId, trackingId = utils.getUniqueId()) {
        const d = api.CommentReply.decode(data)
            .mapLeft(err => new Error(err))
            .unsafeCoerce();
        return FeedbackReply.fromApiServerData(d, feedbackBaseId, trackingId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data, feedbackBaseId, trackingId = utils.getUniqueId()) {
        if (data instanceof api.ExtendedNonDeletedCommentReply) {
            data.author.ifJust(author => {
                store.dispatch('users/addOrUpdateUser', { user: author });
            });
        }
        let comment = '';
        let authorId = null;
        if (!(data instanceof api.DeletedCommentReply)) {
            comment = data.comment;
            authorId = data.authorId.extractNullable();
        }
        return new FeedbackReply(trackingId, data.id, data.inReplyToId.extractNullable(), comment, authorId, data.lastEdit.extractNullable(), data.replyType, data.createdAt, feedbackBaseId, data.commentType, data.approved, data.deleted);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static createEmpty(userId, feedbackBaseId, isPeerFeedback = false, inReplyToId = null) {
        return new FeedbackReply(utils.getUniqueId(), null, inReplyToId, '', userId, null, 'markdown', SiteSettingsStore.currentTimeGetter()(), feedbackBaseId, isPeerFeedback ? 'peer_feedback' : 'normal', isPeerFeedback, false);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get author() {
        return User.findUserById(this.authorId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isEmpty() {
        return this.message === '';
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isPeerFeedback() {
        return this.commentType === 'peer_feedback';
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get focusId() {
        if (this.id == null) {
            return '';
        }
        return `${this.feedbackBaseId}-${this.id}`;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canSeeEdits(assignment) {
        const author = this.author;
        if (author?.isEqualOrMemberOf(NormalUser.getCurrentUser()) ?? false) {
            return true;
        }
        return (assignment.hasPermission(CPerm.can_view_others_comment_edits) ||
            assignment.hasPermission(CPerm.can_view_others_in_section_comment_edits));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canEdit(assignment) {
        const author = this.author;
        if (author?.isEqualOrMemberOf(NormalUser.getCurrentUser()) ?? false) {
            return true;
        }
        return (assignment.hasPermission(CPerm.can_edit_others_comments) ||
            assignment.hasPermission(CPerm.can_edit_others_in_section_comments));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    canApprove(assignment) {
        switch (this.commentType) {
            case 'peer_feedback':
                return assignment.hasPermission(CPerm.can_approve_inline_comments);
            default:
                return false;
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _update(props) {
        return new FeedbackReply(this.trackingId, this.id, 
        // Can't use ?? here because `props.inReplyToId` can be `null`
        // in which case we _do_ want to use the value.
        props.inReplyToId === undefined ? this.inReplyToId : props.inReplyToId, props.message ?? this.message, this.authorId, this.lastEdit, this.replyType, this.createdAt, this.feedbackBaseId, this.commentType, props.approved ?? this.approved, props.deleted ?? this.deleted);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    update(message) {
        return this._update({ message });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    markAsDeleted() {
        return this._update({ deleted: true });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    markAsReplyTo(inReplyToId) {
        return this._update({ inReplyToId });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateFromApiServerData(data) {
        return FeedbackReply.fromApiServerData(data, this.feedbackBaseId, this.trackingId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    approveAndSave(approved) {
        if (this.id == null) {
            return EitherAsync.liftEither(Left(new Error("Cannot change approval of feedback that hasn't been saved.")));
        }
        let meth = client.comment.approveReply;
        if (!approved) {
            meth = client.comment.disapproveReply;
        }
        return meth({
            commentBaseId: this.feedbackBaseId,
            replyId: this.id,
        }).map(response => response.map(cgResult => FeedbackReply.fromApiServerData(cgResult, this.feedbackBaseId, this.trackingId)));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    fetchEdits() {
        if (this.id == null) {
            return EitherAsync.liftEither(Left(new Error("Cannot get edits of feedback that hasn't been saved.")));
        }
        return client.comment
            .getAllReplyEdits({
            commentBaseId: this.feedbackBaseId,
            replyId: this.id,
        })
            .map(response => response.map(cgResult => cgResult.map(FeedbackReplyEdit.fromApiServerData)));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    delete() {
        if (this.id == null) {
            return utils.emptyResponse();
        }
        return client.comment.deleteReply({
            commentBaseId: this.feedbackBaseId,
            replyId: this.id,
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    save() {
        if (this.id == null) {
            return client.comment.createReply({
                commentBaseId: this.feedbackBaseId,
                requestBody: {
                    comment: this.message,
                    inReplyTo: Maybe.fromNullable(this.inReplyToId),
                    replyType: this.replyType,
                },
            });
        }
        else {
            return client.comment.patchReply({
                commentBaseId: this.feedbackBaseId,
                replyId: this.id,
                requestBody: {
                    comment: this.message,
                },
            });
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isShowable(showInlineFeedback) {
        switch (showInlineFeedback) {
            case 'all':
                return true;
            case 'only-normal':
                return this.commentType === 'normal';
            case 'only-peer':
                return this.commentType === 'peer_feedback';
            case 'none':
                return false;
            default:
                throw utils.AssertionError.assertNever(showInlineFeedback);
        }
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class FeedbackThread {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(type, id, replies, workId, fileId, lineNumber) {
        this.type = type;
        this.id = id;
        this.replies = replies;
        this.workId = workId;
        this.fileId = fileId;
        this.lineNumber = lineNumber;
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        this._cache = new CacheDecorator();
        Object.freeze(replies);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data) {
        const { type, id } = data;
        const replies = data.replies.map(r => FeedbackReply.fromApiServerData(r, id));
        switch (type) {
            case 'general-feedback':
                return new FeedbackThread(type, id, replies, data.workId, null, null);
            case 'inline-feedback':
                return new FeedbackThread(type, id, replies, data.workId, data.fileId, data.line);
            default:
                throw assert.AssertionError.assertNever(type);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isEmpty() {
        return this.nonDeletedTopLevelReplies.length === 0;
    }
    get replyCount() {
        return utils.sumMap(this.replies, r => (r.isEmpty || r.deleted ? 0 : 1));
    }
    get location() {
        switch (this.type) {
            case 'general-feedback':
                return Object.freeze({
                    type: this.type,
                    workId: this.workId,
                });
            case 'inline-feedback':
                assert.AssertionError.assert(this.fileId !== null);
                assert.AssertionError.assert(this.lineNumber !== null);
                return Object.freeze({
                    type: this.type,
                    fileId: this.fileId,
                    lineNumber: this.lineNumber,
                });
            default:
                throw assert.AssertionError.assertNever(this.type);
        }
    }
    get topLevelReplies() {
        return this.replies.filter(r => r.inReplyToId == null);
    }
    get nonDeletedTopLevelReplies() {
        return this.topLevelReplies.filter(r => {
            const { id, deleted } = r;
            return !deleted || (id != null && this.repliesTo.get(id).isJust());
        });
    }
    get repliesTo() {
        const repliesTo = new map.MaybeMap();
        for (const reply of this.replies) {
            const { inReplyToId } = reply;
            if (inReplyToId != null) {
                repliesTo
                    .get(inReplyToId)
                    .ifJust(l => l.push(reply))
                    .ifNothing(() => {
                    repliesTo.set(inReplyToId, [reply]);
                });
            }
        }
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const deleteEmptyThreads = (reply) => {
            const { id, deleted } = reply;
            if (id == null || !deleted) {
                return true;
            }
            const replies = repliesTo.get(id).orDefault([]).filter(deleteEmptyThreads);
            if (replies.length === 0) {
                repliesTo.delete(id);
                return false;
            }
            else {
                return true;
            }
        };
        this.topLevelReplies.forEach(deleteEmptyThreads);
        return repliesTo;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _update(replies) {
        return new FeedbackThread(this.type, this.id, replies, this.workId, this.fileId, this.lineNumber);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addReply(newReply) {
        return this._update(this.replies.concat(newReply));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateReply(updatedReply) {
        const replies = this.replies.map(r => {
            if (r.trackingId === updatedReply.trackingId) {
                return updatedReply;
            }
            return r;
        });
        return this._update(replies);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    deleteReply(deletedReply) {
        if (deletedReply.id == null) {
            return this._update(this.replies.filter(r => r.trackingId !== deletedReply.trackingId));
        }
        else {
            return this.updateReply(deletedReply.markAsDeleted());
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    createReply(userId, isPeerFeedback = false, inReplyToId = null) {
        const newReply = FeedbackReply.createEmpty(userId, this.id, isPeerFeedback, inReplyToId);
        return this.addReply(newReply);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static canAddReply(submission, now) {
        const assignment = submission.assignment;
        if (assignment == null) {
            return false;
        }
        const maybeCurrentUser = UserStore.currentUser();
        if (maybeCurrentUser.isNothing()) {
            return false;
        }
        const currentUser = maybeCurrentUser.unsafeCoerce();
        const author = submission.user;
        const perms = [CPerm.can_grade_work];
        if (author.isEqualOrMemberOf(currentUser)) {
            perms.push(CPerm.can_add_own_inline_comments);
        }
        if (perms.some(x => assignment.hasPermission(x))) {
            return true;
        }
        if (assignment.peerFeedbackSettings.isJust()) {
            const myUserId = UserStore.id();
            const userId = SubmissionsStore.getLatestSubmissionByUser()(submission.assignmentId, myUserId).mapOrDefault(utils.attr('userId'), myUserId);
            const connections = PeerFeedbackStore.getConnectionsForUser()(assignment, userId);
            connections.ifNothing(() => {
                utils.sentry.withSentry(sentry => {
                    sentry.captureMessage('Peer feedback connections were not yet loaded');
                });
                PeerFeedbackStore.loadConnectionsForUser({
                    userId,
                    assignment,
                });
            });
            const hasConnection = connections
                .map(conns => conns.some(user => user.id === author.id))
                .orDefault(true);
            if (!hasConnection) {
                return false;
            }
            if (!assignment.deadlinePassed(now)) {
                return false;
            }
            if (assignment.peerFeedbackDeadlinePassed(now)) {
                return false;
            }
            return true;
        }
        return false;
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "replyCount", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "location", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "topLevelReplies", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "nonDeletedTopLevelReplies", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], FeedbackThread.prototype, "repliesTo", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class Feedback {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(general, userLines) {
        this.general = general;
        this.userLines = userLines;
        Object.freeze(userLines);
        this.user = Object.freeze(userLines.reduce((acc, line) => {
            const location = line.location;
            if (location.type === 'general-feedback') {
                return acc;
            }
            let fileIdMap = acc[location.fileId];
            if (fileIdMap == null) {
                fileIdMap = {};
                acc[location.fileId] = fileIdMap;
            }
            fileIdMap[location.lineNumber] = line;
            return acc;
        }, {}));
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get authorIds() {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const getAuthorIds = (thread) => utils.filterMap(thread.replies, reply => Maybe.fromNullable(reply.authorId));
        const authorIds = this.general.reduce((existing, general) => existing.concat(getAuthorIds(general)), utils.flatMap1(this.userLines, getAuthorIds));
        return new Set(authorIds);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data) {
        if (data.authors != null) {
            for (const user of data.authors) {
                store.dispatch('users/addOrUpdateUser', { user });
            }
        }
        const generalFeedback = data.generalComment.map(gc => FeedbackThread.fromApiServerData(gc));
        const userLines = data.user.map(FeedbackThread.fromApiServerData);
        return new Feedback(generalFeedback, userLines);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addFeedbackBase(line) {
        if (line.type !== 'inline-feedback') {
            throw new Error(`The given line does not have the correct type, got ${line.type}`);
        }
        const newLines = [...this.userLines];
        const oldLineIndex = this.userLines.findIndex(l => l.lineNumber === line.lineNumber && l.fileId === line.fileId);
        if (oldLineIndex < 0) {
            newLines.push(line);
        }
        else {
            newLines[oldLineIndex] = line;
        }
        return new Feedback(this.general, newLines);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    removeFeedbackBase(line) {
        if (line.type !== 'inline-feedback') {
            throw new Error(`The given line does not have the correct type, got ${line.type}`);
        }
        return new Feedback(this.general, this.userLines.filter(l => !(l.lineNumber === line.lineNumber && l.fileId === line.fileId)));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getFeedbackLine(fileId, lineNumber) {
        return this.user[fileId][lineNumber];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateThread(thread) {
        if (thread.type === 'general-feedback') {
            return new Feedback(Maybe.of(thread), this.userLines);
        }
        else if (thread.isEmpty) {
            return this.removeFeedbackBase(thread);
        }
        else {
            return this.addFeedbackBase(thread);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    countEntries() {
        return utils.sumMap(Object.values(this.user), file => utils.sumMap(Object.values(file), feedback => feedback.replyCount), this.general.mapOrDefault(g => g.replyCount, 0));
    }
}
